import { useMemo } from 'react';
import { LuLoaderCircle } from 'react-icons/lu';
import type { AnalysisSearch } from '@/routes/agent_spa/agent/analysis';
import useFetchAnalysisFunnel from '../api/useFetchAnalysisFunnel';
import useFetchAnalysisFilterOptions from '../api/useFetchAnalysisFilterOptions';
import { TABLE_STEPS } from '../constants';
import AnalysisFilterBar from './AnalysisFilterBar';
import AnalysisNoData from './AnalysisNoData';
import AnalysisDailyTable from './AnalysisDailyTable';
import AnalysisFunnelChart from './AnalysisFunnelChart';
import AnalysisSummaryCards from './AnalysisSummaryCards';

type Props = {
  search: AnalysisSearch;
  onUpdateSearch: (updates: Partial<AnalysisSearch>) => void;
};

/**
 * 進捗分析タブ
 * AnalysisPage シェルから呼ばれ、フィルター・サマリー・ファネル・日別テーブルを表示する。
 */
export default function AnalysisProgressTab({ search, onUpdateSearch }: Props) {
  const {
    from,
    to,
    granularity,
    metric,
    count_mode: countMode,
    group_by: groupBy,
    filter_organization_user_ids: filterOrgUserIds,
    filter_target: filterTarget,
    filter_company_tag_ids: filterCompanyTagIds,
    filter_job_tag_ids: filterJobTagIds,
    filter_candidate_tag_ids: filterCandidateTagIds,
    visible_steps: visibleSteps,
    page,
  } = search;

  const { data: filterOptions, isLoading: isFilterLoading } = useFetchAnalysisFilterOptions();

  const {
    data: funnelData,
    isLoading: isFunnelLoading,
    isFetching: isFunnelFetching,
    isError,
    error,
  } = useFetchAnalysisFunnel({
    from,
    to,
    granularity,
    metric,
    count_mode: countMode,
    group_by: groupBy,
    filter_organization_user_ids: filterOrgUserIds,
    filter_target: filterTarget,
    filter_company_tag_ids: filterCompanyTagIds,
    filter_job_tag_ids: filterJobTagIds,
    filter_candidate_tag_ids: filterCandidateTagIds,
    visible_steps: visibleSteps,
    page,
    per_page: 30,
  });

  const isLoading = isFunnelLoading || isFilterLoading;
  const hasData = funnelData && funnelData.summary.length > 0;

  // 担当者グループ化時は、フィルター一覧（organization_users）の並び順に系列・凡例を揃える。
  // これによりグラフ凡例とフィルターのプルダウンで担当者の順番が一致する。
  const seriesOrderKeys = useMemo(() => {
    const isUserGroup =
      groupBy === 'candidate_user' || groupBy === 'company_user' || groupBy === 'job_user';
    if (!isUserGroup) return [];
    return (filterOptions?.organization_users ?? []).map((u) => String(u.id));
  }, [groupBy, filterOptions]);

  const filterBar = (
    <div className="sticky top-[60px] z-20 -mx-lg bg-[#f6f6f6] px-lg py-3 shadow-[0_2px_4px_rgba(0,0,0,0.06)]">
      <AnalysisFilterBar
        filterOrganizationUserIds={filterOrgUserIds ?? []}
        organizationUsers={filterOptions?.organization_users ?? []}
        filterCompanyTagIds={filterCompanyTagIds ?? []}
        companyTags={filterOptions?.company_tags ?? []}
        filterJobTagIds={filterJobTagIds ?? []}
        jobTags={filterOptions?.job_tags ?? []}
        filterCandidateTagIds={filterCandidateTagIds ?? []}
        candidateTags={filterOptions?.candidate_tags ?? []}
        groupBy={groupBy}
        metric={metric}
        from={from}
        to={to}
        granularity={granularity}
        countMode={countMode}
        filterTarget={filterTarget}
        onFilterChange={({
          filterTarget: nextFilterTarget,
          organizationUserIds,
          companyTagIds,
          jobTagIds,
          candidateTagIds,
        }) =>
          onUpdateSearch({
            filter_target: nextFilterTarget,
            filter_organization_user_ids: organizationUserIds,
            filter_company_tag_ids: companyTagIds,
            filter_job_tag_ids: jobTagIds,
            filter_candidate_tag_ids: candidateTagIds,
            page: 1,
          })
        }
        onGroupByChange={(v) => onUpdateSearch({ group_by: v, page: 1 })}
        onMetricChange={(v) => onUpdateSearch({ metric: v, page: 1 })}
        onDateRangeChange={(nextFrom, nextTo) =>
          onUpdateSearch({ from: nextFrom, to: nextTo, page: 1 })
        }
        onGranularityChange={(v) => onUpdateSearch({ granularity: v, page: 1 })}
        onCountModeChange={(v) => onUpdateSearch({ count_mode: v, page: 1 })}
      />
    </div>
  );

  return (
    <>
      <div>
        {filterBar}

        <div className="mt-md space-y-4">
          {isLoading && (
            <div
              role="status"
              aria-live="polite"
              aria-label="読み込み中"
              className="flex items-center justify-center py-16"
            >
              <LuLoaderCircle className="size-6 animate-spin text-primary" />
            </div>
          )}

          {!isLoading && isError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-600">
              データの読み込みに失敗しました。時間を置いて再度お試しください。
              {error instanceof Error && (
                <p className="mt-1 text-xs text-red-400">{error.message}</p>
              )}
            </div>
          )}

          {!isLoading && !isError && !hasData && <AnalysisNoData />}

          {!isLoading && !isError && hasData && (
            <AnalysisSummaryCards
              summary={funnelData.summary}
              visibleSteps={visibleSteps ?? TABLE_STEPS.map((ts) => ts.key)}
              onVisibleStepsChange={(steps) => onUpdateSearch({ visible_steps: steps })}
              metric={metric}
            >
              <AnalysisFunnelChart
                summary={funnelData.summary}
                chart={funnelData.chart}
                isGrouped={!!groupBy}
                visibleSteps={visibleSteps ?? TABLE_STEPS.map((ts) => ts.key)}
                metric={metric}
                seriesOrderKeys={seriesOrderKeys}
              />
            </AnalysisSummaryCards>
          )}
        </div>
      </div>

      {!isLoading && !isError && hasData && (
        <div className="mt-4">
          <AnalysisDailyTable
            timeSeries={funnelData.time_series}
            visibleSteps={visibleSteps ?? TABLE_STEPS.map((ts) => ts.key)}
            isGrouped={!!groupBy}
            currentPage={page}
            onPageChange={(p) => onUpdateSearch({ page: p })}
            isFetching={isFunnelFetching}
          />
        </div>
      )}
    </>
  );
}
